import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, XCircle, ShieldCheck, User, HourglassMedium, Warning } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";

/**
 * Command-center strip for supervisors on the dashboard.
 *
 * Shows up to 5 pending HR requests + 5 open safety observations with inline
 * approve/reject/close controls so a shift supervisor can clear their queue
 * without navigating away from `/`. Every action refreshes the affected
 * side so counts stay accurate.
 *
 * Refresh is triggered on `hr-requests-updated` custom events too, so the
 * regular HR page and this panel stay in sync when actions happen elsewhere.
 */
export default function SupervisorInlineActions() {
  const { t } = useLang();
  const [hr, setHr] = useState([]);
  const [obs, setObs] = useState([]);
  const [busy, setBusy] = useState(null);   // hr:{id} | obs:{id}

  const loadHr = useCallback(async () => {
    try {
      const r = await api.get("/hr/requests", { params: { status: "pending" } });
      setHr((r.data || []).slice(0, 5));
    } catch (_e) { setHr([]); }
  }, []);

  const loadObs = useCallback(async () => {
    try {
      const r = await api.get("/hse/observations");
      const open = (r.data || []).filter((o) => o.status !== "closed").slice(0, 5);
      setObs(open);
    } catch (_e) { setObs([]); }
  }, []);

  useEffect(() => {
    loadHr(); loadObs();
    const onHr = () => loadHr();
    window.addEventListener("hr-requests-updated", onHr);
    return () => window.removeEventListener("hr-requests-updated", onHr);
  }, [loadHr, loadObs]);

  const reviewHr = async (id, action) => {
    setBusy(`hr:${id}`);
    try {
      await api.post(`/hr/requests/${id}/${action}`, { note: "" });
      toast.success(action === "approve" ? t("hrReqApprovedToast") : t("hrReqRejectedToast"));
      window.dispatchEvent(new CustomEvent("hr-requests-updated"));
      loadHr();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const closeObs = async (_id) => {
    // Cerrar OCAS requiere foto obligatoria — se hace desde SGSSO → Observaciones,
    // no desde este quick-close. Navegamos al módulo.
    window.location.href = "/hse";
  };

  if (hr.length === 0 && obs.length === 0) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dash-supervisor-inline">
      {hr.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
            <HourglassMedium size={18} weight="duotone" className="text-purple-600" />
            <p className="overline-label !text-purple-800">{t("dashInlineHrTitle")}</p>
          </div>
          <ul className="divide-y divide-slate-100" data-testid="dash-inline-hr-list">
            {hr.map((r) => (
              <li key={r.id} className="p-3 flex items-center gap-3" data-testid={`dash-inline-hr-${r.id}`}>
                <div className="h-8 w-8 rounded-full bg-purple-100 text-purple-800 flex items-center justify-center font-black text-xs shrink-0">
                  <User size={14} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{r.employee_name}</p>
                  <p className="text-[11px] text-slate-500 truncate">
                    <b>{t(`hrReqType${r.type[0].toUpperCase() + r.type.slice(1)}`)}</b> · {r.date_from} → {r.date_to}
                    {r.note ? ` · ${r.note}` : ""}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    className="h-7 gap-1 bg-emerald-600 hover:bg-emerald-700 text-xs"
                    onClick={() => reviewHr(r.id, "approve")}
                    disabled={busy === `hr:${r.id}`}
                    data-testid={`dash-inline-hr-approve-${r.id}`}
                  >
                    <CheckCircle size={12} weight="bold" /> {t("hrReqBtnApprove")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1 text-xs text-red-700 border-red-200 hover:bg-red-50"
                    onClick={() => reviewHr(r.id, "reject")}
                    disabled={busy === `hr:${r.id}`}
                    data-testid={`dash-inline-hr-reject-${r.id}`}
                  >
                    <XCircle size={12} weight="bold" /> {t("hrReqBtnReject")}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {obs.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
            <ShieldCheck size={18} weight="duotone" className="text-amber-600" />
            <p className="overline-label !text-amber-800">{t("dashInlineObsTitle")}</p>
          </div>
          <ul className="divide-y divide-slate-100" data-testid="dash-inline-obs-list">
            {obs.map((o) => {
              const sevColor = o.severity === "high" ? "bg-red-100 text-red-800" :
                               o.severity === "medium" ? "bg-amber-100 text-amber-800" :
                               "bg-emerald-100 text-emerald-800";
              return (
                <li key={o.id} className="p-3 flex items-center gap-3" data-testid={`dash-inline-obs-${o.id}`}>
                  <div className="h-8 w-8 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
                    <Warning size={14} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-slate-900 truncate">{o.area}</p>
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${sevColor}`}>{o.severity}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">{o.description}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50 shrink-0"
                    onClick={() => closeObs(o.id)}
                    disabled={busy === `obs:${o.id}`}
                    data-testid={`dash-inline-obs-close-${o.id}`}
                  >
                    <CheckCircle size={12} weight="bold" /> {t("obsClose")}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
